import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import ReportService, {
  TransactionReport,
  SummaryReport,
  ExportRequest,
  ExportJob,
} from '../../services/reports';

interface ReportState {
  transactionReport: {
    data: TransactionReport | null;
    loading: boolean;
  };
  summaryReport: {
    data: SummaryReport | null;
    loading: boolean;
  };
  settlementReport: {
    data: any;
    loading: boolean;
  };
  disputeReport: {
    data: any;
    loading: boolean;
  };
  feeReport: {
    data: any;
    loading: boolean;
  };
  exports: {
    data: ExportJob[];
    meta: any;
    loading: boolean;
  };
  currentExport: ExportJob | null;
  exporting: boolean;
  downloading: boolean;
  error: string | null;
}

const initialState: ReportState = {
  transactionReport: {
    data: null,
    loading: false,
  },
  summaryReport: {
    data: null,
    loading: false,
  },
  settlementReport: {
    data: null,
    loading: false,
  },
  disputeReport: {
    data: null,
    loading: false,
  },
  feeReport: {
    data: null,
    loading: false,
  },
  exports: {
    data: [],
    meta: null,
    loading: false,
  },
  currentExport: null,
  exporting: false,
  downloading: false,
  error: null,
};

export const fetchTransactionReport = createAsyncThunk(
  'report/fetchTransactionReport',
  async (
    params: { startDate: string; endDate: string; filters?: any },
    { rejectWithValue }
  ) => {
    try {
      return await ReportService.getTransactionReport(
        params.startDate,
        params.endDate,
        params.filters
      );
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch transaction report'
      );
    }
  }
);

export const fetchSummaryReport = createAsyncThunk(
  'report/fetchSummaryReport',
  async (
    params: { startDate: string; endDate: string },
    { rejectWithValue }
  ) => {
    try {
      return await ReportService.getSummaryReport(params.startDate, params.endDate);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch summary report'
      );
    }
  }
);

export const fetchSettlementReport = createAsyncThunk(
  'report/fetchSettlementReport',
  async (
    params: { startDate: string; endDate: string },
    { rejectWithValue }
  ) => {
    try {
      return await ReportService.getSettlementReport(params.startDate, params.endDate);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch settlement report'
      );
    }
  }
);

export const fetchDisputeReport = createAsyncThunk(
  'report/fetchDisputeReport',
  async (
    params: { startDate: string; endDate: string },
    { rejectWithValue }
  ) => {
    try {
      return await ReportService.getDisputeReport(params.startDate, params.endDate);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch dispute report'
      );
    }
  }
);

export const fetchFeeReport = createAsyncThunk(
  'report/fetchFeeReport',
  async (
    params: { startDate: string; endDate: string },
    { rejectWithValue }
  ) => {
    try {
      return await ReportService.getFeeReport(params.startDate, params.endDate);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch fee report'
      );
    }
  }
);

export const exportReport = createAsyncThunk(
  'report/exportReport',
  async (data: ExportRequest, { rejectWithValue }) => {
    try {
      return await ReportService.exportReport(data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to export report'
      );
    }
  }
);

export const fetchExports = createAsyncThunk(
  'report/fetchExports',
  async (
    params: { page?: number; limit?: number },
    { rejectWithValue }
  ) => {
    try {
      return await ReportService.listExports(params.page, params.limit);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch exports'
      );
    }
  }
);

export const fetchExportDetail = createAsyncThunk(
  'report/fetchExportDetail',
  async (id: string, { rejectWithValue }) => {
    try {
      return await ReportService.getExport(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch export'
      );
    }
  }
);

export const downloadReport = createAsyncThunk(
  'report/downloadReport',
  async (
    { id, filename }: { id: string; filename: string },
    { rejectWithValue }
  ) => {
    try {
      await ReportService.downloadReport(id, filename);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to download report'
      );
    }
  }
);

const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentExport: (state) => {
      state.currentExport = null;
    },
    clearTransactionReport: (state) => {
      state.transactionReport.data = null;
    },
    clearSummaryReport: (state) => {
      state.summaryReport.data = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Transaction Report
    builder
      .addCase(fetchTransactionReport.pending, (state) => {
        state.transactionReport.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTransactionReport.fulfilled,
        (state, action: PayloadAction<TransactionReport>) => {
          state.transactionReport.loading = false;
          state.transactionReport.data = action.payload;
        }
      )
      .addCase(fetchTransactionReport.rejected, (state, action) => {
        state.transactionReport.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Summary Report
    builder
      .addCase(fetchSummaryReport.pending, (state) => {
        state.summaryReport.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSummaryReport.fulfilled,
        (state, action: PayloadAction<SummaryReport>) => {
          state.summaryReport.loading = false;
          state.summaryReport.data = action.payload;
        }
      )
      .addCase(fetchSummaryReport.rejected, (state, action) => {
        state.summaryReport.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Settlement Report
    builder
      .addCase(fetchSettlementReport.pending, (state) => {
        state.settlementReport.loading = true;
        state.error = null;
      })
      .addCase(fetchSettlementReport.fulfilled, (state, action: PayloadAction<any>) => {
        state.settlementReport.loading = false;
        state.settlementReport.data = action.payload;
      })
      .addCase(fetchSettlementReport.rejected, (state, action) => {
        state.settlementReport.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Dispute Report
    builder
      .addCase(fetchDisputeReport.pending, (state) => {
        state.disputeReport.loading = true;
        state.error = null;
      })
      .addCase(fetchDisputeReport.fulfilled, (state, action: PayloadAction<any>) => {
        state.disputeReport.loading = false;
        state.disputeReport.data = action.payload;
      })
      .addCase(fetchDisputeReport.rejected, (state, action) => {
        state.disputeReport.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Fee Report
    builder
      .addCase(fetchFeeReport.pending, (state) => {
        state.feeReport.loading = true;
        state.error = null;
      })
      .addCase(fetchFeeReport.fulfilled, (state, action: PayloadAction<any>) => {
        state.feeReport.loading = false;
        state.feeReport.data = action.payload;
      })
      .addCase(fetchFeeReport.rejected, (state, action) => {
        state.feeReport.loading = false;
        state.error = action.payload as string;
      });

    // Export Report
    builder
      .addCase(exportReport.pending, (state) => {
        state.exporting = true;
        state.error = null;
      })
      .addCase(exportReport.fulfilled, (state, action: PayloadAction<ExportJob>) => {
        state.exporting = false;
        state.exports.data.unshift(action.payload);
        state.currentExport = action.payload;
      })
      .addCase(exportReport.rejected, (state, action) => {
        state.exporting = false;
        state.error = action.payload as string;
      });

    // Fetch Exports
    builder
      .addCase(fetchExports.pending, (state) => {
        state.exports.loading = true;
        state.error = null;
      })
      .addCase(
        fetchExports.fulfilled,
        (state, action: PayloadAction<{ data: ExportJob[]; meta: any }>) => {
          state.exports.loading = false;
          state.exports.data = action.payload.data;
          state.exports.meta = action.payload.meta;
        }
      )
      .addCase(fetchExports.rejected, (state, action) => {
        state.exports.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Export Detail
    builder
      .addCase(fetchExportDetail.pending, (state) => {
        state.exports.loading = true;
      })
      .addCase(
        fetchExportDetail.fulfilled,
        (state, action: PayloadAction<ExportJob>) => {
          state.exports.loading = false;
          state.currentExport = action.payload;
          // Update in list if exists
          const index = state.exports.data.findIndex((e) => e.id === action.payload.id);
          if (index !== -1) {
            state.exports.data[index] = action.payload;
          }
        }
      )
      .addCase(fetchExportDetail.rejected, (state, action) => {
        state.exports.loading = false;
        state.error = action.payload as string;
      });

    // Download Report
    builder
      .addCase(downloadReport.pending, (state) => {
        state.downloading = true;
        state.error = null;
      })
      .addCase(downloadReport.fulfilled, (state) => {
        state.downloading = false;
      })
      .addCase(downloadReport.rejected, (state, action) => {
        state.downloading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearCurrentExport,
  clearTransactionReport,
  clearSummaryReport,
} = reportSlice.actions;

export default reportSlice.reducer;
